// frontend/src/pages/CryptoCardShop.jsx
// White center sections · Purple header + footer · BTC ATM image with X

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { usePlaidLink } from 'react-plaid-link';
import { supabase } from '../supabaseClient';
import SignupModal from '../SignupModal';
import LoginModal from '../LoginModal';
import ActivateCardModal from '../ActivateCardModal';
import {
  CreditCard, ShoppingCart, X, Check, Shield, Zap, Globe,
  Lock, ChevronRight, Building, Mail, AlertCircle,
  CheckCircle, Loader, Trash2, Plus, Minus,
  BadgeCheck, TrendingUp, Clock, Menu, ChevronDown,
  Ban, Bitcoin, ArrowRight, Banknote, Timer, Smartphone
} from 'lucide-react';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const API = process.env.REACT_APP_API_URL || 'https://globalpay-backend-v2.onrender.com';

const menuItems = {
  Services: [
    { name:'Send Money',         path:'/send-money' },
    { name:'Crypto Cards',       path:'/crypto-cards' },
    { name:'Digital Wallets',    path:'/digital-wallets' },
    { name:'Business Solutions', path:'/business-solutions' },
  ],
  Company: [
    { name:'About Us',  path:'/about-us' },
    { name:'Careers',   path:'/careers' },
    { name:'Press',     path:'/press' },
    { name:'Investors', path:'/investors' },
  ],
  Support: [
    { name:'Help Center',       path:'/help-center' },
    { name:'Contact Us',        path:'/contact-us' },
    { name:'API Documentation', path:'/api-documentation' },
    { name:'Security',          path:'/security' },
  ],
  Legal: [
    { name:'Terms of Service', path:'/terms-of-service' },
    { name:'Privacy Policy',   path:'/privacy-policy' },
    { name:'Compliance',       path:'/compliance' },
    { name:'Licenses',         path:'/licenses' },
  ],
};

const CARDS = [
  { id:'card_50',  value:50,  badge:null,      desc:'Perfect for everyday purchases',   btcEquiv:'~0.0005 BTC', features:['Instant email delivery','BTC preloaded','No expiry'] },
  { id:'card_100', value:100, badge:'POPULAR', desc:'Best value for most users',        btcEquiv:'~0.001 BTC',  features:['Instant email delivery','BTC preloaded','No expiry'] },
  { id:'card_200', value:200, badge:null,      desc:'For bigger crypto moves',          btcEquiv:'~0.002 BTC',  features:['Instant email delivery','BTC preloaded','No expiry'] },
  { id:'card_500', value:500, badge:'PREMIUM', desc:'Maximum purchasing power',         btcEquiv:'~0.005 BTC',  features:['Instant email delivery','BTC preloaded','Priority support','No expiry'] },
];

// ─── Stripe Card Form ─────────────────────────────────────────
const StripeCardForm = ({ onSuccess, onError, total, email, cart }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { data:{ session } } = await supabase.auth.getSession();
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type:'card', card: elements.getElement(CardElement), billing_details:{ email }
      });
      if (pmError) throw new Error(pmError.message);
      const res = await fetch(`${API}/payments/shop/card`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${session?.access_token}` },
        body: JSON.stringify({ paymentMethodId: paymentMethod.id, amount:total, email, items:cart }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error||'Payment failed'); }
      onSuccess(await res.json());
    } catch(err) { onError(err.message); }
    finally { setProcessing(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-50 rounded-xl border-2 border-slate-200 p-4">
        <CardElement options={{ style:{ base:{ fontSize:'16px', color:'#1e293b', fontFamily:'system-ui,sans-serif', '::placeholder':{ color:'#94a3b8' } }, invalid:{ color:'#ef4444' } } }} />
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-1.5">
        <Lock className="w-3.5 h-3.5 text-emerald-500"/> 256-bit encrypted · Secured by Stripe · Never stored
      </p>
      <button onClick={handlePay} disabled={processing||!stripe}
        className="w-full bg-violet-600 hover:bg-violet-700 text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors shadow-lg">
        {processing ? <><Loader className="w-5 h-5 animate-spin"/>Processing...</> : <><Lock className="w-5 h-5"/>Pay ${total.toFixed(2)} Securely</>}
      </button>
    </div>
  );
};

// ─── Plaid Button ─────────────────────────────────────────────
const PlaidPayButton = ({ onSuccess, onError, total, email, cart }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data:{ session } } = await supabase.auth.getSession();
        const res = await fetch(`${API}/payments/plaid/create-link-token`, {
          method:'POST', headers:{ Authorization:`Bearer ${session?.access_token}`, 'Content-Type':'application/json' }
        });
        if (!res.ok) throw new Error('Init failed');
        const data = await res.json();
        setLinkToken(data.data.linkToken);
      } catch(err) { onError(err.message); }
      finally { setFetching(false); }
    })();
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken) => {
      setLoading(true);
      try {
        const { data:{ session } } = await supabase.auth.getSession();
        const res = await fetch(`${API}/payments/shop/bank`, {
          method:'POST',
          headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${session?.access_token}` },
          body: JSON.stringify({ publicToken, amount:total, email, items:cart }),
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.error||'Payment failed'); }
        onSuccess(await res.json());
      } catch(err) { onError(err.message); }
      finally { setLoading(false); }
    },
  });

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 flex items-center gap-1.5">
        <Shield className="w-3.5 h-3.5 text-blue-500"/> Securely link your bank via Plaid · Direct ACH transfer
      </p>
      <button onClick={() => ready && open()} disabled={!ready||fetching||loading}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors shadow-lg">
        {fetching||loading
          ? <><Loader className="w-5 h-5 animate-spin"/>{fetching?'Initializing...':'Processing...'}</>
          : <><Building className="w-5 h-5"/>Link Bank &amp; Pay ${total.toFixed(2)}</>}
      </button>
    </div>
  );
};

// ─── Checkout Modal (white theme) ────────────────────────────
const CheckoutModal = ({ cart, onClose, onSuccess, userEmail }) => {
  const [payMethod, setPayMethod] = useState('card');
  const [email, setEmail] = useState(userEmail||'');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const total = cart.reduce((s,i) => s+i.value*i.qty, 0);
  const cartItems = cart.filter(i => i.qty > 0);
  const handleSuccess = () => { setDone(true); setTimeout(() => { onSuccess(); onClose(); }, 3000); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-violet-100 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-violet-600"/>
            </div>
            <h2 className="text-lg font-bold text-slate-900">Complete Purchase</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
            <X className="w-5 h-5 text-slate-400"/>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {done ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600"/>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Payment Successful!</h3>
              <p className="text-slate-500 text-sm">Your crypto cards will be emailed to <strong>{email}</strong> shortly.</p>
            </div>
          ) : (
            <>
              {/* Order summary */}
              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Order Summary</h3>
                {cartItems.map(item => (
                  <div key={item.id} className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">{item.qty}× ${item.value} Card</span>
                    <span className="font-semibold text-slate-900">${(item.value*item.qty).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between">
                  <span className="font-bold text-slate-900">Total</span>
                  <span className="font-extrabold text-violet-700 text-lg">${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Delivery email */}
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                  <Mail className="w-3 h-3 inline mr-1"/> Delivery Email
                </label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"/>
                <p className="text-xs text-slate-400 mt-1.5">Cards will be emailed here after payment</p>
              </div>

              {/* Payment method tabs */}
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                  Payment Method
                </label>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <button type="button" onClick={() => setPayMethod('card')}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${payMethod==='card'?'border-violet-500 bg-violet-50':'border-slate-200 bg-white hover:border-slate-300'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${payMethod==='card'?'bg-violet-100':'bg-slate-100'}`}>
                      <CreditCard className={`w-4 h-4 ${payMethod==='card'?'text-violet-600':'text-slate-400'}`}/>
                    </div>
                    <div className="font-semibold text-slate-800 text-sm">Credit / Debit</div>
                    <div className="text-xs text-slate-500 mt-0.5">Visa, Mastercard, Amex</div>
                    {payMethod==='card' && <Check className="w-4 h-4 text-violet-600 mt-2"/>}
                  </button>

                  <button type="button" onClick={() => setPayMethod('bank')}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${payMethod==='bank'?'border-blue-500 bg-blue-50':'border-slate-200 bg-white hover:border-slate-300'}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${payMethod==='bank'?'bg-blue-100':'bg-slate-100'}`}>
                      <Building className={`w-4 h-4 ${payMethod==='bank'?'text-blue-600':'text-slate-400'}`}/>
                    </div>
                    <div className="font-semibold text-slate-800 text-sm">Bank Account</div>
                    <div className="text-xs text-slate-500 mt-0.5">ACH via Plaid</div>
                    {payMethod==='bank' && <Check className="w-4 h-4 text-blue-600 mt-2"/>}
                  </button>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl mb-4 flex items-center gap-2 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0"/>{error}
                  </div>
                )}

                {payMethod==='card'
                  ? <Elements stripe={stripePromise}><StripeCardForm total={total} email={email} cart={cartItems} onSuccess={handleSuccess} onError={setError}/></Elements>
                  : <PlaidPayButton total={total} email={email} cart={cartItems} onSuccess={handleSuccess} onError={setError}/>
                }
              </div>

              <div className="flex items-center justify-center gap-4 text-xs text-slate-400 pt-2 border-t border-slate-100">
                <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5"/> Secure</span>
                <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5"/> Instant delivery</span>
                <span className="flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5"/> FinCEN licensed</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const CryptoCardShop = () => {
  const [cart, setCart] = useState(CARDS.map(c => ({ ...c, qty:0 })));
  const [showCheckout, setShowCheckout] = useState(false);
  const [user, setUser] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showActivateCardModal, setShowActivateCardModal] = useState(false);
  const [loginStep, setLoginStep] = useState('email');
  const [signupData, setSignupData] = useState({ firstName:'', lastName:'', email:'', phone:'', country:'US', password:'', confirmPassword:'', code:'', generatedCode:'' });
  const [loginData, setLoginData] = useState({ email:'', phone:'', loginMethod:'email', code:'', generatedCode:'' });
  const [activateData, setActivateData] = useState({ cardNumber:'', pin:'', code:'', generatedCode:'' });

  const countries = [
    { code:'US', name:'United States', flag:'🇺🇸', requiresSSN:true },
    { code:'GB', name:'United Kingdom', flag:'🇬🇧', requiresSSN:false },
    { code:'NG', name:'Nigeria', flag:'🇳🇬', requiresSSN:false },
    { code:'GH', name:'Ghana', flag:'🇬🇭', requiresSSN:false },
    { code:'CA', name:'Canada', flag:'🇨🇦', requiresSSN:false },
    { code:'AU', name:'Australia', flag:'🇦🇺', requiresSSN:false },
  ];

  useEffect(() => {
    supabase.auth.getSession().then(({ data:{ session } }) => {
      if (session?.user) setUser(session.user);
    });
  }, []);

  const updateQty = (id, delta) => setCart(prev => prev.map(c => c.id===id ? { ...c, qty:Math.max(0,c.qty+delta) } : c));
  const cartItems = cart.filter(c => c.qty > 0);
  const cartCount = cart.reduce((s,c) => s+c.qty, 0);
  const cartTotal = cart.reduce((s,c) => s+c.value*c.qty, 0);
  const toggleSubmenu = (menu) => setExpandedMenu(expandedMenu===menu ? null : menu);

  return (
    <div className="min-h-screen">

      {/* ══════════════════════════════════════════
          HEADER — exact App.js purple gradient
      ══════════════════════════════════════════ */}
      <div className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900">
        <header className="bg-purple-900/50 backdrop-blur-sm border-b border-purple-700 sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center">
                <img src="/zanors-logo-full.png" alt="Zanors Logo"
                  className="h-16 md:h-20 mix-blend-normal" style={{ backgroundColor:'transparent' }}/>
              </Link>
              <div className="flex items-center space-x-3">
                <Link to="/shop" className="text-orange-400 hover:text-orange-300 font-semibold text-sm md:text-base transition-colors flex items-center gap-2">
                  <ShoppingCart size={18}/> Shop Cards
                </Link>
                <button onClick={() => setShowSignupModal(true)} className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium text-sm text-white transition-colors">Sign Up</button>
                <button onClick={() => setShowLoginModal(true)} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium text-sm text-white transition-colors">Login</button>
                <button onClick={() => setShowActivateCardModal(true)} className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-lg font-medium text-sm text-white transition-colors">Activate Card</button>
                {cartCount > 0 && (
                  <button onClick={() => setShowCheckout(true)}
                    className="relative bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-lg font-bold text-sm text-white transition-colors flex items-center gap-2">
                    <ShoppingCart size={18}/>
                    <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">{cartCount}</span>
                  </button>
                )}
                <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 hover:bg-purple-700 rounded-lg transition-colors text-white">
                  {isMenuOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
                </button>
              </div>
            </div>
          </div>

          {isMenuOpen && (
            <div className="bg-purple-800 border-t border-purple-700 max-h-96 overflow-y-auto">
              <div className="container mx-auto px-4 py-4">
                <Link to="/shop" onClick={() => setIsMenuOpen(false)}
                  style={{ background:'linear-gradient(90deg,rgba(212,168,50,0.2),rgba(245,166,35,0.2))', borderLeft:'3px solid #D4A832' }}
                  className="w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 hover:bg-purple-700/50 mb-4">
                  <ShoppingCart size={20} className="text-orange-500"/>
                  <div>
                    <div className="font-semibold text-orange-400">Shop Crypto Cards</div>
                    <div className="text-xs text-purple-300">$50, $100, $200, $500 · Instant delivery</div>
                  </div>
                </Link>
                <div className="border-t border-purple-600 my-2"/>
                {Object.entries(menuItems).map(([cat, items]) => (
                  <div key={cat} className="mb-2">
                    <button onClick={() => toggleSubmenu(cat)} className="w-full flex items-center justify-between p-3 hover:bg-purple-700 rounded-lg text-white">
                      <span className="font-semibold text-lg">{cat}</span>
                      <ChevronDown className={`w-5 h-5 transition-transform ${expandedMenu===cat?'rotate-180':''}`}/>
                    </button>
                    {expandedMenu===cat && (
                      <div className="ml-4 mt-2 space-y-1 bg-purple-900/50 rounded-lg p-2">
                        {items.map(item => (
                          <Link key={item.name} to={item.path} onClick={() => setIsMenuOpen(false)}
                            className="block p-3 hover:bg-purple-700 rounded-lg text-gray-200 hover:text-white">{item.name}</Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </header>

        {/* HERO — inside purple section */}
        <div className="container mx-auto px-6 pt-16 pb-20 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 rounded-full px-4 py-1.5 text-sm font-semibold text-orange-300 mb-6">
            <Zap className="w-4 h-4"/> Instant Email Delivery · No Account Required
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold mb-5 tracking-tight">
            Shop Crypto <span className="text-orange-400">Prepaid Cards</span>
          </h1>
          <p className="text-purple-200 text-xl max-w-2xl mx-auto leading-relaxed">
            Buy prepaid cards loaded with Bitcoin. Pay with your bank or card — delivered instantly to your inbox.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-10 text-sm text-purple-300">
            {[{icon:Shield,txt:'Bank-level security'},{icon:Zap,txt:'Instant delivery'},{icon:Globe,txt:'120+ countries'},{icon:BadgeCheck,txt:'FinCEN licensed'}]
              .map(({icon:Icon,txt}) => (
              <div key={txt} className="flex items-center gap-2"><Icon className="w-4 h-4 text-orange-400"/>{txt}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════
          WHITE CENTER — card grid + all content
      ══════════════════════════════════════════ */}
      <div className="bg-slate-50">

        {/* Card Grid */}
        <div className="container mx-auto px-6 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cart.map(card => (
              <div key={card.id} className={`relative bg-white rounded-3xl border-2 overflow-hidden flex flex-col shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
                card.badge==='POPULAR' ? 'border-violet-400' :
                card.badge==='PREMIUM' ? 'border-amber-400' :
                'border-slate-200'
              }`}>
                {card.badge && (
                  <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-extrabold z-10 ${
                    card.badge==='POPULAR' ? 'bg-violet-600 text-white' : 'bg-amber-500 text-white'
                  }`}>
                    {card.badge==='POPULAR'?'🔥 ':'⭐ '}{card.badge}
                  </div>
                )}

                {/* Card face */}
                <div className={`relative h-48 flex flex-col justify-between p-5 ${
                  card.value===50  ? 'bg-gradient-to-br from-violet-500 via-purple-600 to-pink-600' :
                  card.value===100 ? 'bg-gradient-to-br from-indigo-500 via-violet-600 to-purple-700' :
                  card.value===200 ? 'bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600' :
                                     'bg-gradient-to-br from-amber-500 via-orange-600 to-rose-600'
                }`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <img src="/zanors-icon.png" alt="Zanors" className="w-8 h-8 object-contain opacity-90"/>
                      <div className="text-white/60 text-xs mt-1 font-medium">Crypto Prepaid Card</div>
                    </div>
                    <CreditCard className="w-7 h-7 text-white/50"/>
                  </div>
                  <div>
                    <div className="text-white/60 text-xs mb-0.5">USD Balance</div>
                    <div className="text-white text-4xl font-extrabold">${card.value}</div>
                    <div className="text-white/60 text-xs mt-0.5">{card.btcEquiv}</div>
                  </div>
                  <div className="absolute bottom-14 right-5 w-8 h-5 rounded bg-yellow-400/30 border border-yellow-400/40"/>
                </div>

                {/* Card body */}
                <div className="p-5 flex-1 flex flex-col">
                  <p className="text-slate-500 text-sm mb-4">{card.desc}</p>
                  <ul className="space-y-1.5 mb-5 flex-1">
                    {card.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-xs text-slate-600">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0"/>{f}
                      </li>
                    ))}
                  </ul>

                  {card.qty===0 ? (
                    <button onClick={() => updateQty(card.id,1)}
                      className={`w-full py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                        card.badge==='POPULAR' ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-lg shadow-violet-200' :
                        card.badge==='PREMIUM' ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-200' :
                        'bg-slate-900 hover:bg-slate-800 text-white'
                      }`}>
                      <ShoppingCart className="w-4 h-4"/> Add to Cart
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button onClick={() => updateQty(card.id,-1)}
                        className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center flex-shrink-0">
                        {card.qty===1 ? <Trash2 className="w-4 h-4 text-red-500"/> : <Minus className="w-4 h-4 text-slate-600"/>}
                      </button>
                      <div className="flex-1 text-center">
                        <div className="font-extrabold text-slate-900 text-xl">{card.qty}</div>
                        <div className="text-xs text-slate-400">${(card.value*card.qty).toFixed(0)} total</div>
                      </div>
                      <button onClick={() => updateQty(card.id,1)}
                        className="w-10 h-10 rounded-xl bg-violet-600 hover:bg-violet-700 flex items-center justify-center flex-shrink-0">
                        <Plus className="w-4 h-4 text-white"/>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Floating checkout bar */}
          {cartCount > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
              <button onClick={() => setShowCheckout(true)}
                className="flex items-center gap-4 bg-slate-900 text-white px-8 py-4 rounded-2xl shadow-2xl hover:bg-slate-800 transition-all font-bold">
                <span className="flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-orange-400"/>{cartCount} item{cartCount>1?'s':''}</span>
                <div className="w-px h-5 bg-white/20"/>
                <span className="text-orange-400">${cartTotal.toFixed(2)}</span>
                <div className="bg-orange-500 text-white text-sm font-bold px-4 py-1.5 rounded-xl flex items-center gap-1.5">
                  Checkout <ChevronRight className="w-4 h-4"/>
                </div>
              </button>
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════
            BTC ATM REPLACEMENT SECTION — white bg
        ══════════════════════════════════════════ */}
        <div className="border-t border-slate-200 py-24 bg-white">
          <div className="container mx-auto px-6">

            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-orange-100 border border-orange-300 rounded-full px-4 py-1.5 text-xs font-bold text-orange-700 uppercase tracking-widest mb-6">
                <Bitcoin className="w-4 h-4"/> The Future of Bitcoin Access
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
                Forget the{' '}
                <span className="relative inline-block">
                  <span className="text-slate-400">BTC ATM.</span>
                  <span className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full h-1 bg-red-500 rounded-full transform -rotate-6"/>
                  </span>
                </span>
                <br/>
                <span className="text-violet-600">Meet Your New Go-To.</span>
              </h2>
              <p className="text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
                Bitcoin ATMs are becoming obsolete — charging up to{' '}
                <strong className="text-red-500">20% in fees</strong> and hard to find.
                Zanors crypto cards give you instant BTC access anywhere, any time.
              </p>
            </div>

            {/* Main visual comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center max-w-6xl mx-auto mb-20">

              {/* LEFT — BTC ATM image with giant red X */}
              <div className="relative flex flex-col items-center">
                <div className="relative w-full max-w-sm mx-auto">
                  {/* Image container */}
                  <div className="relative rounded-3xl overflow-hidden border-4 border-red-200 shadow-xl">
                    <img src="/btc-atm-machines.png" alt="Bitcoin ATMs"
                      className="w-full object-cover opacity-60"/>
                    {/* Giant red X overlay */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative w-48 h-48">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-48 h-8 bg-red-600 rounded-full shadow-2xl transform rotate-45 opacity-90"/>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-48 h-8 bg-red-600 rounded-full shadow-2xl transform -rotate-45 opacity-90"/>
                        </div>
                        {/* Circle outline */}
                        <div className="absolute inset-0 rounded-full border-8 border-red-600 opacity-90"/>
                      </div>
                    </div>
                    {/* Tinted overlay */}
                    <div className="absolute inset-0 bg-red-900/30"/>
                  </div>

                  {/* Person with ATM image below */}
                  <div className="relative mt-4 rounded-2xl overflow-hidden border-2 border-red-100 shadow-md opacity-50">
                    <img src="/btc-atm-person.png" alt="Person at Bitcoin ATM"
                      className="w-full object-cover" style={{maxHeight:'160px', objectPosition:'top'}}/>
                    <div className="absolute inset-0 bg-red-100/40"/>
                  </div>
                </div>

                {/* Old way label */}
                <div className="mt-5 flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-5 py-2">
                  <Ban className="w-4 h-4 text-red-500"/>
                  <span className="font-bold text-red-600 text-sm">Old Way — Slow, Expensive, Inconvenient</span>
                </div>
              </div>

              {/* RIGHT — Feature comparison */}
              <div>
                <div className="bg-slate-50 rounded-3xl border border-slate-200 p-8">
                  <h3 className="text-xl font-extrabold text-slate-900 mb-6">BTC ATM vs Zanors Card</h3>

                  <div className="space-y-4">
                    {[
                      { label:'Transaction Fees',     atm:'Up to 20%',          zanors:'0% hidden fees' },
                      { label:'Availability',         atm:'Limited locations',   zanors:'Global — online 24/7' },
                      { label:'Payment Methods',      atm:'Cash only',           zanors:'Card or bank account' },
                      { label:'Wait Time',            atm:'Queue + machine wait',zanors:'Under 60 seconds' },
                      { label:'Delivery',             atm:'Physical receipt',    zanors:'Instant email delivery' },
                      { label:'Safety',               atm:'Risky locations',     zanors:'Encrypted & licensed' },
                    ].map(row => (
                      <div key={row.label} className="grid grid-cols-3 gap-2 items-center text-sm border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                        <div className="font-semibold text-slate-600 text-xs uppercase tracking-wide">{row.label}</div>
                        <div className="bg-red-50 text-red-600 rounded-xl px-3 py-1.5 text-center text-xs font-semibold border border-red-100">
                          ✗ {row.atm}
                        </div>
                        <div className="bg-emerald-50 text-emerald-700 rounded-xl px-3 py-1.5 text-center text-xs font-semibold border border-emerald-100">
                          ✓ {row.zanors}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 3 stat cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                { icon:Timer,      stat:'< 60s',   label:'Time to get BTC',         sub:'vs 10+ min at an ATM' },
                { icon:Banknote,   stat:'0%',      label:'Hidden fees',              sub:'vs up to 20% at ATMs' },
                { icon:Smartphone, stat:'100%',    label:'Online — no travel',       sub:'vs finding a working ATM' },
              ].map(({ icon:Icon, stat, label, sub }) => (
                <div key={label} className="bg-white border-2 border-slate-200 rounded-2xl p-6 text-center shadow-sm hover:shadow-md hover:border-violet-300 transition-all">
                  <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-violet-600"/>
                  </div>
                  <div className="text-4xl font-extrabold text-violet-600 mb-1">{stat}</div>
                  <div className="font-bold text-slate-900 mb-1">{label}</div>
                  <div className="text-xs text-slate-400">{sub}</div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <button onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}
                className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white font-bold px-8 py-4 rounded-2xl text-lg transition-all shadow-lg shadow-violet-200">
                Get Your BTC Card Now <ArrowRight className="w-5 h-5"/>
              </button>
              <p className="text-slate-400 text-sm mt-3">Instant delivery · Accepted worldwide · No hidden fees</p>
            </div>
          </div>
        </div>

        {/* Why Zanors */}
        <div className="container mx-auto px-6 py-20">
          <h2 className="text-3xl font-extrabold text-slate-900 text-center mb-2">Why buy from Zanors?</h2>
          <p className="text-slate-500 text-center mb-12">The trusted platform for crypto prepaid cards</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon:Zap,        title:'Instant Delivery',     desc:'Cards arrive in your inbox within seconds of payment.',      color:'text-amber-600 bg-amber-50 border-amber-100' },
              { icon:Shield,     title:'Bank-Grade Security',  desc:'256-bit encryption, FinCEN licensed, US regulated.',         color:'text-emerald-600 bg-emerald-50 border-emerald-100' },
              { icon:Globe,      title:'Global Acceptance',    desc:'Use your crypto card for purchases in 120+ countries.',       color:'text-blue-600 bg-blue-50 border-blue-100' },
              { icon:Lock,       title:'Hassle-Free Purchase', desc:'Buy instantly with a simple, streamlined checkout process.',  color:'text-violet-600 bg-violet-50 border-violet-100' },
              { icon:Clock,      title:'24/7 Support',         desc:'Our team is always available to assist your purchase.',       color:'text-rose-600 bg-rose-50 border-rose-100' },
              { icon:TrendingUp, title:'Live BTC Rates',       desc:'Priced at real-time Bitcoin exchange rates every time.',      color:'text-indigo-600 bg-indigo-50 border-indigo-100' },
            ].map(({ icon:Icon, title, desc, color }) => (
              <div key={title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md hover:border-violet-200 transition-all">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 border ${color}`}>
                  <Icon className="w-6 h-6"/>
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* End white center */}

      {/* ══════════════════════════════════════════
          FOOTER — exact App.js  bg-black/50 + purple
      ══════════════════════════════════════════ */}
      <footer className="bg-black bg-opacity-50 mt-0 py-12" style={{ background: 'rgba(0,0,0,0.85)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {Object.entries(menuItems).map(([section, links]) => (
              <div key={section}>
                <h4 className="font-bold text-lg mb-4 text-white">{section}</h4>
                <ul className="space-y-2 text-purple-300">
                  {links.map(item => (
                    <li key={item.name}>
                      <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-purple-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="w-14 h-14 rounded-full flex items-center justify-center p-2 border border-purple-600 shadow-lg flex-shrink-0">
                  <img src="/zanors-icon.png" alt="Zanors Icon" className="w-full h-full object-contain"/>
                </div>
                <div>
                  <span className="text-white font-bold">Zanors</span>
                  <p className="text-xs text-purple-400">Zanors is a financial technology platform. Zanors is not an FDIC-insured Bank or depositary</p>
                  <p className="text-xs text-purple-400">taking institution. In the USA, banking services for the placement of deposits where the customers'</p>
                  <p className="text-xs text-purple-400">funds may be placed are provided by BankProv, Member FDIC. Deposit insurance coverage only</p>
                  <p className="text-xs text-purple-400">protects against the failure of an FDIC insured depository institution. Crypto assets and</p>
                  <p className="text-xs text-purple-400">non-bank digital wallet are not insured by the FDIC, are not deposits, and may lose value.</p>
                </div>
              </div>
              <div className="text-center md:text-right flex-shrink-0">
                <p className="text-purple-300 text-sm">© 2026 Zanors Inc. All rights reserved.</p>
                <p className="text-purple-400 text-xs mt-1">Licensed by FinCEN · Regulated by NMLS · Member FDIC</p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {showCheckout && (
        <CheckoutModal
          cart={cart}
          onClose={() => setShowCheckout(false)}
          onSuccess={() => setCart(CARDS.map(c => ({ ...c, qty:0 })))}
          userEmail={user?.email||''}
        />
      )}

      <SignupModal
        showSignupModal={showSignupModal}
        setShowSignupModal={setShowSignupModal}
        signupData={signupData}
        setSignupData={setSignupData}
        countries={countries}
        setShowLandingPage={() => {}}
      />
      <LoginModal
        showLoginModal={showLoginModal}
        setShowLoginModal={setShowLoginModal}
        loginStep={loginStep}
        setLoginStep={setLoginStep}
        loginData={loginData}
        setLoginData={setLoginData}
        generateAuthCode={() => {}}
        setShowLandingPage={() => {}}
      />
      <ActivateCardModal
        showActivateCardModal={showActivateCardModal}
        setShowActivateCardModal={setShowActivateCardModal}
        activateData={activateData}
        setActivateData={setActivateData}
        onSuccess={() => {}}
      />
    </div>
  );
};

export default CryptoCardShop;